import { Link } from "react-router-dom";

import tellUsImg from "@/shared/assets/illustrations/tell-us.svg";
import createAccountImg from "@/shared/assets/illustrations/create-account.svg";
import tfaImg from "@/shared/assets/illustrations/2fa.svg";
import { LEGAL_ROUTES } from "@/shared/constants/paths";
import type { FlowSection } from "@/shared/constants/flowTypes";
import { getEnvironment } from "@/shared/hooks/useGetEnvironment";
import { CreateAccount } from "@/pages/onboarding/components/screens/CreateAccount";

import { createAboutYourself } from "./components/AboutYourself";
import { createInviteLanding } from "./components/InviteLanding";
import { createInviteLogin } from "./components/InviteLogin";
import { createInviteVerifyEmail } from "./components/InviteVerifyEmail";

interface InviteAuthInfo {
  isExistingUser: boolean;
  invitationEmail: string | null;
}

export function createInviteAcceptanceScreens(
  inviterName: string,
  decedentName: string,
  token: string,
  auth: InviteAuthInfo,
): FlowSection {
  const isNewUser = !auth.isExistingUser;

  return {
    id: "invite-acceptance",
    title: "",
    allowSaveAndClose: false,
    saveOnEachStep: false,
    screens: [
      {
        id: "invite-landing",
        layout: "full",
        maxWidth: "4xl",
        verticalCenter: true,
        showHeaderLogo: true,
        customComponent: createInviteLanding({ inviterName, decedentName }),
        hideButton: true,
      },
      {
        id: "about-yourself",
        layout: "full",
        textAlign: "center",
        showHeaderLogo: true,
        imageSrc: tellUsImg,
        imageSize: "sm",
        title: "Tell us about yourself",
        customComponent: createAboutYourself(decedentName),
        validate: (answers) => {
          const errors: Record<string, string> = {};
          const name = (answers["invitee-full-name"] as string) ?? "";
          const phone = (answers["invitee-phone"] as string) ?? "";
          const relationship = (answers["invitee-relationship"] as string) ?? "";

          const nameWords = name.trim().split(/\s+/);
          if (!name.trim()) {
            errors["invitee-full-name"] = "Full name is required";
          } else if (nameWords.length < 2 || nameWords.some((w) => w.length === 0)) {
            errors["invitee-full-name"] = "Please enter first and last name";
          }

          const phoneDigits = phone.replace(/\D/g, "");
          if (!phone.trim()) {
            errors["invitee-phone"] = "Phone number is required";
          } else if (phoneDigits.length !== 10) {
            errors["invitee-phone"] = "Please enter a valid 10-digit phone number";
          }

          if (!relationship) {
            errors["invitee-relationship"] = "Please select a relationship";
          }

          return errors;
        },
        getButtonText: () => "Continue",
        shouldDisplay: () => isNewUser,
      },
      {
        id: "create-account",
        layout: "full",
        textAlign: "center",
        showHeaderLogo: true,
        imageSrc: createAccountImg,
        imageSize: "sm",
        title: "Create your account",
        customComponent: CreateAccount,
        hideButton: true,
        note: {
          className: "mt-4 text-2xs",
          content: (
            <>
              By signing up, you agree to our{" "}
              <Link
                to={LEGAL_ROUTES.TERMS_OF_SERVICE}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-800 underline"
              >
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link
                to={LEGAL_ROUTES.PRIVACY_POLICY}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-800 underline"
              >
                Privacy Policy
              </Link>
              .
            </>
          ),
        },
        shouldDisplay: () => isNewUser,
      },
      {
        id: "verify-email",
        layout: "full",
        showHeaderLogo: true,
        imageSrc: tfaImg,
        imageSize: "sm",
        textAlign: "center",
        verticalCenter: true,
        title: "Check your email",
        subtitle: "Enter the 6-digit code we sent you.",
        customComponent: createInviteVerifyEmail(token),
        hideButton: true,
        shouldDisplay: () => {
          const env = getEnvironment();
          return isNewUser && env !== "local";
        },
      },
      {
        id: "invite-login",
        layout: "full",
        textAlign: "center",
        showHeaderLogo: true,
        title: "Welcome back",
        subtitle: "Sign in to accept the invitation and join the estate.",
        customComponent: createInviteLogin(token, auth.invitationEmail ?? ""),
        hideButton: true,
        shouldDisplay: () => auth.isExistingUser,
      },
    ],
  };
}
